<template>
  <Teleport v-if="isOpen" to="body" :disabled="noOverlay">
    <div
      class="fixed md:absolute inset-0 flex flex-col justify-end z-50 pt-safe-area-top overflow-visible"
    >
      <div
        class="opacity-50 bg-black fixed inset-0 z-40"
        :class="{ 'md:hidden': noOverlay, 'hidden md:block': mobileFullscreen }"
      />
      <Transition
        :enter-active-class="
          'transition ' + (mobileFullscreen ? 'duration-0 md:duration-300' : 'duration-300')
        "
        :leave-active-class="
          'transition ' + (mobileFullscreen ? 'duration-0 md:duration-300' : 'duration-300')
        "
        enter-from-class="translate-y-full md:translate-y-10 opacity-0"
        leave-to-class="translate-y-full md:translate-y-10 opacity-0"
        appear
        @after-leave="isOpen = false"
      >
        <div
          v-if="showPopover"
          ref="popover"
          class="z-50 md:absolute overflow-hidden md:overflow-visible w-full md:w-auto"
          :class="[
            popoverClass,
            mobileFullscreen ? 'h-full md:h-auto md:shadow-3' : 'shadow-3 mt-10 md:mt-0',
          ]"
        >
          <span class="hidden md:block arrow shadow-3" ref="arrow" />
          <button class="block md:hidden absolute right-0 mt-5 mr-2 p-2" @click.prevent>
            <svg role="presentation" focusable="false" tabindex="-1" class="icon icon-sm"   alt="close" svg-inline  xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 32 32"><title>close</title>
  <path fill="currentColor" d="M24.63 3.06a3.05 3.05 0 1 1 4.31 4.3L7.37 28.95a3.05 3.05 0 0 1-4.31-4.3L24.63 3.05Z"/>
  <path fill="currentColor" d="M28.94 24.63a3.05 3.05 0 0 1-4.3 4.31L3.05 7.37a3.05 3.05 0 1 1 4.3-4.31l21.58 21.57Z"/>
</svg>
          </button>
          <slot />
        </div>
      </Transition>
    </div>
  </Teleport>
</template>

<script lang="ts" setup>
import debounce from 'lodash/debounce'

const X_OFFSET = 10
const Y_OFFSET = 5
const MIN_WIDTH = 356
const ARROW_WIDTH = 16

const props = defineProps<{
  open?: boolean
  anchorEl?: HTMLElement
  popoverClass?: string
  noOverlay?: boolean
  mobileFullscreen?: boolean
}>()

const isOpen = ref(props.open)
const showPopover = ref(true)

watch(
  () => props.open,
  async (val) => {
    if (val) {
      isOpen.value = true
      showPopover.value = true
    } else if (isOpen.value) {
      showPopover.value = false
    }
    await nextTick(() => {
      updatePosition()
    })
  },
  { immediate: true },
)

const resizeHandler = debounce(updatePosition, 200)

onMounted(() => {
  window.addEventListener('resize', resizeHandler)
})

onBeforeUnmount(() => {
  window.removeEventListener('resize', resizeHandler)
})

const popover = useTemplateRef('popover')
const arrow = useTemplateRef('arrow')

function updatePosition() {
  if (!props.open || !props.anchorEl) {
    return
  }

  if (!showPopover.value || !popover.value || !arrow.value) {
    return
  }

  // Reset values for drawer style
  if (document.body.clientWidth < 768) {
    popover.value.style.width = ''
    popover.value.style.top = ''
    popover.value.style.left = ''
    return
  }

  const rect = props.anchorEl.getBoundingClientRect()

  const top = props.noOverlay
    ? rect.height - Y_OFFSET
    : rect.top + window.pageYOffset + rect.height - Y_OFFSET
  popover.value.style.top = `${top}px`

  const width = getWidth(rect)
  popover.value.style.width = `${width}px`

  if (!props.noOverlay) {
    // When we use overlay, the popover is mounted to the body (so relative to the window)
    const contentLeft = getContentLeft(rect, width)
    if (contentLeft + width + X_OFFSET > document.body.clientWidth) {
      arrow.value.style.right = `${
        document.body.clientWidth - rect.left - rect.width / 2 - X_OFFSET
      }px`
      arrow.value.style.left = `auto`
      popover.value.style.left = `${document.body.clientWidth - width - X_OFFSET}px`
    } else if (contentLeft < 0) {
      arrow.value.style.left = `${getArrowLeft(width)}px`
      popover.value.style.left = `${X_OFFSET}px`
    } else {
      arrow.value.style.left = `${getArrowLeft(width)}px`
      popover.value.style.left = `${contentLeft}px`
    }
  } else {
    // When we do not use overlay the popover is relative to the anchorEl
    const halfWidth = width / 2
    const halfRect = rect.width / 2
    if (rect.x + halfRect + halfWidth > document.body.clientWidth) {
      arrow.value.style.left = `auto`
      arrow.value.style.right = `${document.body.clientWidth - rect.left - halfRect - X_OFFSET}px`
      popover.value.style.left = `${document.body.clientWidth - rect.x - width - X_OFFSET}px`
    } else {
      arrow.value.style.left = `${getArrowLeft(width)}px`
      popover.value.style.left = `${halfRect - halfWidth}px`
    }
  }
}

function getWidth(rect: DOMRect) {
  const width = Math.max(rect.width + X_OFFSET * 2, MIN_WIDTH)
  return Math.min(width, document.body.clientWidth - X_OFFSET)
}

function getContentLeft(rect: DOMRect, popoverWidth: number) {
  return rect.left + (rect.width - popoverWidth) / 2
}

function getArrowLeft(popoverWidth: number) {
  return (popoverWidth - ARROW_WIDTH) / 2
}
</script>

<style scoped>
@screen sm {
  .arrow {
    @apply absolute bg-white w-4 h-4 -top-1.5 left-1/2 -z-1;
    transform: rotateZ(-45deg) skew(20deg, 20deg);
  }
}
</style>
